import { Button, Card, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
	type CashBasisPaymentParameters,
	type CashBasisPaymentRateType,
	type CashBasisPaymentReportFormat,
	cashBasisPaymentRateTypes,
} from '@parkchirp/reporting';
import { useMemo, useState } from 'react';
import { AccountFilter, type AccountFilterValue } from './AccountFilter';
import { DateRangeFilter, type DateRangeValue } from './DateRangeFilter';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import { RateTypesFilter } from './RateTypesFilter';
import { paymentReportsInitialDateRange } from './shared';
import { reportingPanelStyles } from './styles';
import type { ReportFormProps } from './types';

const initialFacilityHierarchy: FacilityHierarchyFilterValue = {
	areas: [],
	branches: [],
	districts: [],
	facilities: [],
};

const defaultRateTypes: CashBasisPaymentRateType[] = [...cashBasisPaymentRateTypes];

export const CashBasisPaymentReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateRange, setDateRange] = useState<DateRangeValue>(paymentReportsInitialDateRange);
	const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: 'all' });
	const [rateTypes, setRateTypes] = useState<CashBasisPaymentRateType[]>(defaultRateTypes);
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(initialFacilityHierarchy);
	const [format, setFormat] = useState<CashBasisPaymentReportFormat>('csv');

	const canSubmit = useMemo(() => {
		return rateTypes.length > 0 && Boolean(dateRange.transactionStartDate);
	}, [dateRange, rateTypes]);

	const reset = () => {
		setDateRange(paymentReportsInitialDateRange);
		setAccountFilter({ type: 'all' });
		setRateTypes(defaultRateTypes);
		setFacilityHierarchy(initialFacilityHierarchy);
		setFormat('csv');
	};

	const buildAccountFilter = (): CashBasisPaymentParameters['accountFilter'] => {
		switch (accountFilter.type) {
			case 'accountId':
				return {
					type: 'accountId',
					value: Number(accountFilter.value),
				};

			case 'subscriptionName':
				return {
					type: 'subscriptionName',
					value: accountFilter.value.trim(),
				};
			default:
				return undefined;
		}
	};

	const buildParameters = (): CashBasisPaymentParameters => {
		const accountFilterValue = buildAccountFilter();

		if (!dateRange.transactionStartDate) {
			throw new Error('Select a transaction start date.');
		}

		const parameters: CashBasisPaymentParameters = {
			rateTypes,
			transactionStartDate: dateRange.transactionStartDate.format('YYYY-MM-DD'),
			format,
		};

		if (dateRange.transactionEndDate) {
			parameters.transactionEndDate = dateRange.transactionEndDate.format('YYYY-MM-DD');
		}

		if (accountFilterValue) {
			parameters.accountFilter = accountFilterValue;
		}

		applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

		if (facilityHierarchy.facilities.length === 1) {
			const facilityNumber = facilityHierarchy.facilities[0].facilityNumber;
			if (facilityNumber !== undefined && facilityNumber !== null && `${facilityNumber}` !== '') {
				parameters.facilityNumber = facilityNumber;
			}
		}

		return parameters;
	};

	const validate = () => {
		if (rateTypes.length === 0) {
			onValidationError('Select at least one rate type.');
			return false;
		}

		if (!dateRange.transactionStartDate) {
			onValidationError('Select a transaction start date.');
			return false;
		}

		if (
			dateRange.transactionEndDate &&
			dateRange.transactionEndDate.isBefore(dateRange.transactionStartDate, 'day')
		) {
			onValidationError('End date must be on or after the start date.');
			return false;
		}

		if (accountFilter.type === 'accountId' && !accountFilter.value) {
			onValidationError('Enter an account ID.');
			return false;
		}

		if (accountFilter.type === 'subscriptionName' && !accountFilter.value.trim()) {
			onValidationError('Enter a subscription name.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType: 'cashBasisPayment',
			parameters: buildParameters(),
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<DateRangeFilter value={dateRange} onChange={setDateRange} showApplicationDate={false} />

				<Divider />

				<AccountFilter filter={accountFilter} onChange={setAccountFilter} />

				<Divider />

				<RateTypesFilter
					availableRateTypes={cashBasisPaymentRateTypes}
					selectedRateTypes={rateTypes}
					onChange={(value) => setRateTypes(value as CashBasisPaymentRateType[])}
				/>

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Output
					</Typography>

					<ToggleButtonGroup
						exclusive
						size='small'
						value={format}
						onChange={(_, nextFormat: CashBasisPaymentReportFormat | null) => {
							if (nextFormat) {
								setFormat(nextFormat);
							}
						}}
					>
						<ToggleButton value='csv'>CSV</ToggleButton>
						<ToggleButton value='pdf'>PDF</ToggleButton>
						<ToggleButton value='xlsx'>XLSX</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				<Divider />

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
